import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { connectRedux } from '../../helpers/connectRedux'
import { Button, InfoNote, Spinner, ValidatedInput } from '../../components'
import CountryPickerModal from '../CountryPickerModal'
import countries from '../../countries.json'
import { isEmailValid, isTelephoneValid } from '../../helpers'
import { appsUsernameSelected, willSendNewVerificationCodeKey, defaultPhoneCountryCode } from '../../helpers/app'
import { ICountry } from '../../models/country'
import { LiteralsService } from '../../services'
import {
  resendVerificationCodeAction,
  setVerificationCallingCodeAction,
  setVerificationCountryAction,
  setVerificationEmailAction,
  setVerificationPhoneAction
} from '../../store/actions'
import { IStoreState } from '../../store/states'

const isPhoneVerification = appsUsernameSelected?.username === 'phone'

interface IProps extends DispatchProp<AnyAction> {
  language: string
  email: string
  phone: string
  isSendingNewCode: boolean
  country?: ICountry
}

/** Asking for a new verification code, shared by the page and the guest prompt. */
class VerificationResendForm extends PureComponent<IProps> {
  constructor(props: IProps) {
    super(props)
    if (isPhoneVerification && !props.country) {
      const defaultCountry = countries.find(country => country.code === defaultPhoneCountryCode)
      if (defaultCountry) {
        props.dispatch(setVerificationCountryAction(defaultCountry))
        props.dispatch(setVerificationCallingCodeAction(defaultCountry.dial_code))
      }
    }
  }

  private get buttonIsDisabled(): boolean {
    const { email, phone, isSendingNewCode } = this.props

    if (isPhoneVerification) return !isTelephoneValid(phone) || isSendingNewCode
    return !isEmailValid(email) || isSendingNewCode
  }

  private get verificationButton(): ReactNode {
    if (this.props.isSendingNewCode) return <div className="loading"><Spinner /></div>
    return (
      <Button
        title={ LiteralsService.get('continue') }
        onPress={ this.handleValidatedButtonPress }
        filled
        upper
        isDisabled={ this.buttonIsDisabled }
      />
    )
  }

  public render(): ReactNode {
    const { email, phone, country } = this.props

    return (
      <>
        <section>
          {
            isPhoneVerification ? (
              <ValidatedInput
                name="phone"
                phone
                label={ LiteralsService.get('phone', true) }
                value={ phone }
                country={ country }
                onChange={ this.onPhoneChange }
                error={ !isTelephoneValid(phone) }
              />
            ) : (
              <ValidatedInput
                name="email"
                label={ LiteralsService.get('email', true) }
                value={ email }
                onChange={ this.onEmailChange }
                error={ !isEmailValid(email) }
              />
            )
          }
          <InfoNote literal={ willSendNewVerificationCodeKey } />
        </section>
        { isPhoneVerification && <CountryPickerModal selectCountry={ this.onCountryChange } selectedCountry={ this.props.country } /> }
        <section className="actions">
          { this.verificationButton }
        </section>
      </>
    )
  }

  private onEmailChange = (email: string) => {
    this.props.dispatch(setVerificationEmailAction(email))
  }

  private onPhoneChange = (phone: string) => {
    this.props.dispatch(setVerificationPhoneAction(phone))
  }

  private onCountryChange = (country: ICountry) => {
    const { dispatch } = this.props
    dispatch(setVerificationCountryAction(country))
    dispatch(setVerificationCallingCodeAction(country.dial_code))
  }

  private handleValidatedButtonPress = () => {
    this.props.dispatch(resendVerificationCodeAction())
  }
}

const mapStateToProps = ({ verification, signUp, configuration }: IStoreState) => ({
  // Redraws the literals when the language selector changes the language.
  language: configuration.language,
  email: verification.email ? verification.email : signUp.email,
  phone: verification.phone ? verification.phone : signUp.phone,
  isSendingNewCode: verification.isSendingNewCode,
  country: verification.country
})

export default connectRedux(mapStateToProps, VerificationResendForm) as React.ComponentType<{}>
